import type {
  AdsAssistantCategoryRow,
  AdsAssistantPostTagDefRow,
  AdsAssistantTemplateRow,
} from "@/lib/adsAssistantClient";
import { compactInputClass, ghostBtnClass } from "@/components/common/surfaceClasses";
import { AdsAssistantManualFormHeader } from "@/components/ads-assistant/AdsAssistantManualFormHeader";
import { AdsAssistantManualPostTextField } from "@/components/ads-assistant/AdsAssistantManualPostTextField";
import { AdsAssistantManualSlotFillSection } from "@/components/ads-assistant/AdsAssistantManualSlotFillSection";
import { AdsAssistantPostTagPickFieldset } from "@/components/ads-assistant/AdsAssistantPostTagPickFieldset";
import { AdsAssistantIngestTemplateizeButton } from "@/components/ads-assistant/AdsAssistantIngestTemplateizeButton";
import { AdsAssistantPlatformCtaField } from "@/components/ads-assistant/AdsAssistantPlatformCtaField";
import { AdsAssistantManualIngestQualityFieldset } from "@/components/ads-assistant/AdsAssistantManualIngestQualityFieldset";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { AdsAssistantManualPostMediaField } from "@/components/ads-assistant/AdsAssistantManualPostMediaField";
import type { AdsPlatformSelectOption } from "@/lib/orgAdsPlatformOptions";

export function AdsAssistantAggregationManualForm(props: {
  /** When set, used for Org Activity "Log now" scroll target. */
  formId?: string;
  platformSelectRef?: React.RefObject<HTMLSelectElement | null>;
  canIngestPosts: boolean;
  canQuality: boolean;
  /** When false (sales-only), hide all slot-fill UI; slot_fill_json may still be sent if populated elsewhere. */
  canSeeSlotLabeledFields: boolean;
  /** When false, hide Graph / external id; client auto-generates external_post_id on save. */
  canSeeGraphExternalId: boolean;
  /** True when the rep may override `published_at`; sales role hides the entire field. */
  canSeePublishedAtOverride: boolean;
  publishOverrideOpen: boolean;
  setPublishOverrideOpen: (v: boolean) => void;
  platformOptions: AdsPlatformSelectOption[];
  platform: string;
  setPlatform: (v: string) => void;
  publishedAt: string;
  setPublishedAt: (v: string) => void;
  platformCta: string;
  setPlatformCta: (v: string) => void;
  externalId: string;
  setExternalId: (v: string) => void;
  body: string;
  setBody: (v: string) => void;
  creativeDescription: string;
  setCreativeDescription: (v: string) => void;
  categories: AdsAssistantCategoryRow[];
  toggleCat: (id: string) => void;
  selectedCats: Set<string>;
  postTagDefs: AdsAssistantPostTagDefRow[];
  selectedPostTags: Set<string>;
  togglePostTag: (id: string) => void;
  templates: AdsAssistantTemplateRow[];
  templateId: string;
  setTemplateId: (v: string) => void;
  slotFillJsonText: string;
  setSlotFillJsonText: (v: string) => void;
  promotedExemplar: boolean;
  setPromotedExemplar: (v: boolean) => void;
  qualityRating: "" | "good" | "bad";
  setQualityRating: (v: "" | "good" | "bad") => void;
  qualityNote: string;
  setQualityNote: (v: string) => void;
  saving: boolean;
  onManualSubmit: (e: React.FormEvent) => void | Promise<void>;
  manualPostMediaFiles: File[];
  setManualPostMediaFiles: (files: File[]) => void;
  /** When wrapped in AdsAssistantManualIngestionBox. */
  embeddedInShell?: boolean;
}) {
  const {
    formId,
    platformSelectRef,
    canIngestPosts,
    canQuality,
    canSeeSlotLabeledFields,
    canSeeGraphExternalId,
    canSeePublishedAtOverride,
    publishOverrideOpen,
    setPublishOverrideOpen,
    platformOptions,
    platform,
    setPlatform,
    publishedAt,
    setPublishedAt,
    platformCta,
    setPlatformCta,
    externalId,
    setExternalId,
    body,
    setBody,
    creativeDescription,
    setCreativeDescription,
    categories,
    toggleCat,
    selectedCats,
    postTagDefs,
    selectedPostTags,
    togglePostTag,
    templates,
    templateId,
    setTemplateId,
    slotFillJsonText,
    setSlotFillJsonText,
    promotedExemplar,
    setPromotedExemplar,
    qualityRating,
    setQualityRating,
    qualityNote,
    setQualityNote,
    saving,
    onManualSubmit,
    manualPostMediaFiles,
    setManualPostMediaFiles,
    embeddedInShell,
  } = props;

  const selectedTemplateSlotsSchema = templates.find((t) => t.id === templateId)?.slots_schema;

  return (
    <form
      id={formId}
      onSubmit={(e) => void onManualSubmit(e)}
      className={embeddedInShell ? "space-y-2" : "rounded-xl border border-gray-200 bg-gray-50/90 px-3 py-3 space-y-2 dark:border-gray-600 dark:bg-zinc-900/40"}
    >
      {!embeddedInShell ? <AdsAssistantManualFormHeader canIngestPosts={canIngestPosts} /> : null}
      {embeddedInShell && !canIngestPosts ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50/90 px-2 py-1.5 text-[11px] text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          Your account can use the Ads library and quality labels, but <strong>logging new posts</strong> needs one of:
          sales, marketing, trainer, admin, owner, dev, or platform. Ask an admin to add a role if you should submit ads.
        </p>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="text-[11px] text-gray-600">
          Platform
          <select
            ref={platformSelectRef}
            className={`mt-0.5 w-full ${compactInputClass}`}
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
          >
            {platformOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {canSeePublishedAtOverride ? (
        <details
          className="rounded border border-dashed border-gray-200 dark:border-gray-700 px-2 py-1.5"
          open={publishOverrideOpen}
          onToggle={(e) => setPublishOverrideOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary className="cursor-pointer text-[11px] font-medium text-gray-700 dark:text-gray-300">
            Override publish time (advanced)
          </summary>
          <div className="mt-2 space-y-1">
            <label className="text-[11px] text-gray-600 dark:text-gray-400 block">
              Published (local)
              <input
                type="datetime-local"
                className="mt-0.5 w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-zinc-900"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
              />
            </label>
            {publishedAt.trim() ? (
              <p className="text-[10px] text-amber-700 dark:text-amber-300">
                This post will be marked as backfilled by you (audit trail captured).
              </p>
            ) : (
              <p className="text-[10px] text-gray-500 dark:text-gray-400">
                Leave blank to publish at the same instant the rep clicks save (default).
              </p>
            )}
          </div>
        </details>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-2">
        <AdsAssistantPlatformCtaField
          platform={platform}
          value={platformCta}
          onChange={setPlatformCta}
          disabled={!canIngestPosts || saving}
        />
      </div>
      {canSeeGraphExternalId ? (
        <label className="text-[11px] text-gray-600 block">
          External id (optional — leave blank to auto-generate)
          <input
            className={`mt-0.5 w-full font-mono ${compactInputClass}`}
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            placeholder="Graph id or leave empty"
          />
        </label>
      ) : null}
      <AdsAssistantManualPostTextField body={body} setBody={setBody} />
      <label className="text-[11px] text-gray-600 block">
        Picture / creative (what it shows or what you&apos;re aiming for)
        <textarea
          className={`mt-0.5 w-full min-h-[56px] ${compactInputClass}`}
          value={creativeDescription}
          onChange={(e) => setCreativeDescription(e.target.value)}
          placeholder="e.g. corner shot of two-story model, people on porch, spring promo text overlay"
        />
      </label>
      <AdsAssistantManualPostMediaField
        disabled={!canIngestPosts || saving}
        files={manualPostMediaFiles}
        onFilesChange={setManualPostMediaFiles}
      />
      {categories.length ? (
        <fieldset>
          <legend className="text-[11px] font-medium text-gray-700">Categories</legend>
          <div className="mt-1 flex flex-wrap gap-2">
            {categories.map((c) => (
              <label
                key={c.id}
                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs ${ghostBtnClass}`}
              >
                <input type="checkbox" checked={selectedCats.has(c.id)} onChange={() => toggleCat(c.id)} />
                {c.label}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}
      <AdsAssistantPostTagPickFieldset
        postTagDefs={postTagDefs}
        selectedPostTags={selectedPostTags}
        togglePostTag={togglePostTag}
      />
      {templates.length ? (
        <label className="text-[11px] text-gray-600 block">
          Template (optional)
          <select
            className={`mt-0.5 w-full ${compactInputClass}`}
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <option value="">—</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {canQuality ? (
        <AdsAssistantManualIngestQualityFieldset
          qualityRating={qualityRating}
          setQualityRating={setQualityRating}
          qualityNote={qualityNote}
          setQualityNote={setQualityNote}
          promotedExemplar={promotedExemplar}
          setPromotedExemplar={setPromotedExemplar}
        />
      ) : null}
      <AdsAssistantIngestTemplateizeButton
        canIngestPosts={canIngestPosts}
        hasTemplates={templates.length > 0}
        adCopy={body}
        creativeDescription={creativeDescription}
        hintTemplateId={templateId}
        templateId={templateId}
        slotFillJsonText={slotFillJsonText}
        setTemplateId={setTemplateId}
        setSlotFillJsonText={setSlotFillJsonText}
      />
      <AdsAssistantManualSlotFillSection
        canSeeSlotLabeledFields={canSeeSlotLabeledFields}
        canIngestPosts={canIngestPosts}
        slotsSchema={selectedTemplateSlotsSchema}
        slotFillJsonText={slotFillJsonText}
        setSlotFillJsonText={setSlotFillJsonText}
      />

      <button
        type="submit"
        {...{ [FIREFLY_TARGET_ATTR]: "ads-log-post" }}
        disabled={saving || !body.trim() || !canIngestPosts}
        className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save post & run analysis"}
      </button>
    </form>
  );
}
